/**
 * Created: 2026-10-27
 * Initial commit: Add web event management page (feat/event/mr-56)
 *
 * Event management page: lists the current site's events in a grid,
 * filtered by the available / in-progress / completed tabs.
 */

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Alert, Box, Button, Chip, Tab, Tabs, Typography } from '@mui/material'
import { DataGridPro, type GridColDef, type GridRowParams } from '@mui/x-data-grid-pro'
import {
  EVENT_STATUS_COLOR,
  EVENT_STATUS_LABEL,
  getFormatLabel,
  SPORT_LABEL,
} from '@tc-app/shared/labels'
import { addHours, format } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import { useRef, useState } from 'react'
import EventDialog from '../../components/EventDialog'
import EventFormDialog from '../../components/EventFormDialog'
import {
  EventFilterTab,
  SiteEventsDocument,
  type SiteEventsQuery,
} from '../../gql/graphql'
import { useRole } from '../../hooks'

gql`
  query SiteEvents(
    $siteId: UUID!
    $tab: EventFilterTab
    $first: Int
    $after: String
  ) {
    events(siteId: $siteId, tab: $tab, first: $first, after: $after) {
      edges {
        node {
          id
          name
          location
          scheduledStartTime
          duration
          drawSize
          minRating
          maxRating
          fee
          numSignups
          status
          gameFormat
          sport
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

type EventRow = SiteEventsQuery['events']['edges'][number]['node']

const TABS: { value: EventFilterTab; label: string }[] = [
  { value: EventFilterTab.Available, label: '可报名' },
  { value: EventFilterTab.InProgress, label: '进行中' },
  { value: EventFilterTab.Ended, label: '已结束' },
]

const formatEventTime = (start: Date, duration: number) =>
  `${format(start, 'yyyy-MM-dd E HH:mm', { locale: zhCN })} - ` +
  format(addHours(start, duration), 'HH:mm')

const columns: GridColDef<EventRow>[] = [
  {
    field: '__rowNumber',
    headerName: '#',
    width: 50,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
  },
  { field: 'name', headerName: '名称', flex: 1, minWidth: 160, sortable: false },
  {
    field: 'gameFormat',
    headerName: '类型',
    width: 120,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (_value, row) =>
      `${SPORT_LABEL[row.sport]} · ${getFormatLabel(row.gameFormat)}`,
  },
  {
    field: 'scheduledStartTime',
    headerName: '时间',
    width: 220,
    sortable: false,
    headerAlign: 'center',
    valueGetter: (value: string, row) => formatEventTime(new Date(value), row.duration),
  },
  { field: 'location', headerName: '地点', width: 140, sortable: false },
  {
    field: 'rating',
    headerName: '等级',
    width: 90,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (_value, row) =>
      row.minRating != null && row.maxRating != null
        ? `${row.minRating.toFixed(1)}-${row.maxRating.toFixed(1)}`
        : '',
  },
  {
    field: 'numSignups',
    headerName: '报名',
    width: 80,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (value: number, row) =>
      row.drawSize != null ? `${value}/${row.drawSize}` : `${value}`,
  },
  {
    field: 'fee',
    headerName: '费用',
    width: 90,
    sortable: false,
    align: 'right',
    headerAlign: 'center',
    valueGetter: (value: number) => `¥${value}`,
  },
  {
    field: 'status',
    headerName: '状态',
    width: 100,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ row }) => (
      <Chip
        label={EVENT_STATUS_LABEL[row.status]}
        color={EVENT_STATUS_COLOR[row.status]}
        size="small"
      />
    ),
  },
]

export default function EventsPage() {
  const { currentSite } = useRole()
  const [tab, setTab] = useState<EventFilterTab>(EventFilterTab.Available)
  const [eventDialogId, setEventDialogId] = useState<string | null>(null)
  const [createEventOpen, setCreateEventOpen] = useState(false)
  const loadingMore = useRef(false)

  const { data, loading, error, fetchMore, refetch } = useQuery(SiteEventsDocument, {
    variables: { siteId: currentSite?.id as string, tab },
    fetchPolicy: 'cache-and-network',
    skip: !currentSite,
  })

  const handleRowsScrollEnd = () => {
    const pageInfo = data?.events.pageInfo
    if (!pageInfo?.hasNextPage || loadingMore.current) return
    loadingMore.current = true
    fetchMore({
      variables: { after: pageInfo.endCursor },
      updateQuery: (prev, { fetchMoreResult }) => ({
        ...prev,
        events: {
          ...fetchMoreResult.events,
          edges: [...prev.events.edges, ...fetchMoreResult.events.edges],
        },
      }),
    }).finally(() => {
      loadingMore.current = false
    })
  }

  const rows = data?.events.edges.map((e) => e.node) ?? []

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4">活动管理</Typography>
        <Box sx={{ flex: 1 }} />
        <Tabs value={tab} onChange={(_e, v: EventFilterTab) => setTab(v)}>
          {TABS.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={!currentSite}
        >
          刷新
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateEventOpen(true)}
          disabled={!currentSite}
        >
          新建活动
        </Button>
      </Box>

      {error && <Alert severity="error">加载活动失败: {error.message}</Alert>}

      <DataGridPro
        rows={rows}
        columns={columns}
        getRowId={(row: EventRow) => row.id}
        loading={loading}
        hideFooter
        disableRowSelectionOnClick
        disableColumnMenu
        density="compact"
        onRowsScrollEnd={handleRowsScrollEnd}
        onRowClick={(params: GridRowParams<EventRow>) =>
          setEventDialogId(params.row.id)
        }
        localeText={{ noRowsLabel: '暂无活动' }}
        sx={{ flex: 1, minHeight: 0, '& .MuiDataGrid-row': { cursor: 'pointer' } }}
      />

      {eventDialogId && (
        <EventDialog
          key={eventDialogId}
          eventId={eventDialogId}
          onClose={() => setEventDialogId(null)}
          // Edits or a cancel may move the event to another tab
          onChanged={() => refetch()}
        />
      )}

      {createEventOpen && currentSite && (
        <EventFormDialog
          slots={[]}
          siteId={currentSite.id}
          onClose={() => setCreateEventOpen(false)}
          onSuccess={() => {
            setCreateEventOpen(false)
            refetch()
          }}
        />
      )}
    </Box>
  )
}
